//src/components/personnel/PersonnelAbsenceFormModal.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  LuCalendarDays,
  LuClock3,
  LuSave,
  LuTrash2,
  LuTruck,
  LuUser,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import {
  createPersonnelAbsence,
  updatePersonnelAbsence,
  deletePersonnelAbsence,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

const ABSENCE_TYPES = [
  "HOLIDAY",
  "DAY_OFF",
  "SICK_LEAVE",
  "UNPAID_LEAVE",
  "PARENTAL_LEAVE",
  "BEREAVEMENT",
  "TRAINING",
  "OTHER",
];

const pad2 = (
  value
) =>
  String(value)
    .padStart(
      2,
      "0"
    );

const toDateInput =
  (
    value
  ) => {
    if (!value) {
      return "";
    }

    const date =
      new Date(value);

    return [
      date.getFullYear(),
      pad2(
        date.getMonth() +
          1
      ),
      pad2(
        date.getDate()
      ),
    ].join("-");
  };

const toTimeInput =
  (
    value
  ) => {
    if (!value) {
      return "09:00";
    }

    const date =
      new Date(value);

    return [
      pad2(
        date.getHours()
      ),
      pad2(
        date.getMinutes()
      ),
    ].join(":");
  };

const todayInput =
  () => {
    const now =
      new Date();

    return [
      now.getFullYear(),
      pad2(
        now.getMonth() +
          1
      ),
      pad2(
        now.getDate()
      ),
    ].join("-");
  };

const buildLocalDate =
  (
    dateValue,
    timeValue
  ) => {
    if (!dateValue) {
      return null;
    }

    const [
      year,
      month,
      day,
    ] =
      dateValue
        .split("-")
        .map(Number);

    const [
      hours,
      minutes,
    ] =
      String(
        timeValue ||
          "00:00"
      )
        .split(":")
        .map(Number);

    return new Date(
      year,
      month - 1,
      day,
      hours || 0,
      minutes || 0,
      0,
      0
    );
  };

export default function PersonnelAbsenceFormModal({
  people = [],
  request = null,
  canDelete = false,
  onClose,
  onChanged,
}) {
  const {
    t,
  } =
    useTranslation(
      "personnel"
    );

  const isEdit =
    !!request?.id;

  const [
    personType,
    setPersonType,
  ] =
    useState(
      request?.personType ||
        "USER"
    );

  const [
    personId,
    setPersonId,
  ] =
    useState(
      request?.personId
        ? String(
            request.personId
          )
        : ""
    );

  const [
    absenceType,
    setAbsenceType,
  ] =
    useState(
      request?.absenceType ||
        "HOLIDAY"
    );

  const [
    isFullDay,
    setIsFullDay,
  ] =
    useState(
      request?.isFullDay ??
        true
    );

  const [
    startDate,
    setStartDate,
  ] =
    useState(
      request?.startAt
        ? toDateInput(
            request.startAt
          )
        : todayInput()
    );

  const [
    endDate,
    setEndDate,
  ] =
    useState(
      request?.endAt
        ? toDateInput(
            request.endAt
          )
        : todayInput()
    );

  const [
    startTime,
    setStartTime,
  ] =
    useState(
      request?.startAt
        ? toTimeInput(
            request.startAt
          )
        : "09:00"
    );

  const [
    endTime,
    setEndTime,
  ] =
    useState(
      request?.endAt
        ? toTimeInput(
            request.endAt
          )
        : "18:00"
    );

  const [
    reason,
    setReason,
  ] =
    useState(
      request?.reason ||
        ""
    );

  const [
    privateNotes,
    setPrivateNotes,
  ] =
    useState(
      request?.privateNotes ||
        ""
    );

  const [
    saving,
    setSaving,
  ] =
    useState(
      false
    );

  const [
    deleting,
    setDeleting,
  ] =
    useState(
      false
    );

  const [
    confirmDelete,
    setConfirmDelete,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const availablePeople =
    useMemo(
      () =>
        people.filter(
          (
            person
          ) =>
            person.personType ===
            personType
        ),
      [
        people,
        personType,
      ]
    );

  const selectedPerson =
    useMemo(
      () =>
        people.find(
          (
            person
          ) =>
            person.personType ===
              personType &&
            Number(
              person.personId
            ) ===
              Number(
                personId
              )
        ) ||
        null,
      [
        people,
        personType,
        personId,
      ]
    );

  const buildPayload =
    () => {
      if (!personId) {
        throw new Error(
          t(
            "requests.form.errors.person_required"
          )
        );
      }

      if (
        !startDate ||
        !endDate
      ) {
        throw new Error(
          t(
            "requests.form.errors.dates_required"
          )
        );
      }

      let start;
      let end;

      if (isFullDay) {
        start =
          buildLocalDate(
            startDate,
            "00:00"
          );

        end =
          buildLocalDate(
            endDate,
            "23:59"
          );

        end.setSeconds(
          59,
          999
        );
      } else {
        if (
          !startTime ||
          !endTime
        ) {
          throw new Error(
            t(
              "requests.form.errors.times_required"
            )
          );
        }

        start =
          buildLocalDate(
            startDate,
            startTime
          );

        end =
          buildLocalDate(
            endDate,
            endTime
          );
      }

      if (
        !start ||
        !end ||
        Number.isNaN(
          start.getTime()
        ) ||
        Number.isNaN(
          end.getTime()
        )
      ) {
        throw new Error(
          t(
            "requests.form.errors.invalid_period"
          )
        );
      }

      if (
        end <= start
      ) {
        throw new Error(
          t(
            "requests.form.errors.end_after_start"
          )
        );
      }

      return {
        personType,

        personId:
          Number(
            personId
          ),

        absenceType,

        startAt:
          start.toISOString(),

        endAt:
          end.toISOString(),

        isFullDay,

        reason:
          reason.trim() ||
          null,

        privateNotes:
          privateNotes.trim() ||
          null,
      };
    };

  const handleSave =
    async () => {
      setError(
        null
      );

      setConfirmDelete(
        false
      );

      try {
        const payload =
          buildPayload();

        setSaving(
          true
        );

        if (isEdit) {
          await updatePersonnelAbsence(
            request.id,
            payload
          );
        } else {
          await createPersonnelAbsence(
            payload
          );
        }

        /*
         * Requests has one general cache.
         * Calendar has year-scoped caches.
         */
        markPersonnelResourceStale(
          "absenceRequests"
        );

        markPersonnelResourceStale(
          "absences"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              isEdit
                ? "updated"
                : "created",
          });
        }

        onClose();
      } catch (
        saveError
      ) {
        console.error(
          "[PersonnelAbsenceForm] save failed:",
          saveError
        );

        setError(
          saveError
            ?.response
            ?.data
            ?.error ||
          saveError
            ?.message ||
          t(
            "requests.form.errors.save_failed"
          )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  const handleDelete =
    async () => {
      if (
        !canDelete ||
        !isEdit ||
        request.status !==
          "PENDING"
      ) {
        return;
      }

      setError(
        null
      );

      try {
        setDeleting(
          true
        );

        await deletePersonnelAbsence(
          request.id
        );

        markPersonnelResourceStale(
          "absenceRequests"
        );

        markPersonnelResourceStale(
          "absences"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              "deleted",
          });
        }

        onClose();
      } catch (
        deleteError
      ) {
        console.error(
          "[PersonnelAbsenceForm] delete failed:",
          deleteError
        );

        setError(
          deleteError
            ?.response
            ?.data
            ?.error ||
          deleteError
            ?.message ||
          t(
            "requests.form.errors.delete_failed"
          )
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  const busy =
    saving ||
    deleting;

  return (
    <div
      className="personnel-absence-form-overlay"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
            event.currentTarget &&
          !busy
        ) {
          onClose();
        }
      }}
    >
      <div
        className="personnel-absence-form-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="personnel-absence-form-header">
          <div>
            <span>
              {isEdit
                ? t(
                    "requests.form.edit_kicker"
                  )
                : t(
                    "requests.form.create_kicker"
                  )}
            </span>

            <h3>
              {isEdit
                ? t(
                    "requests.form.edit_title"
                  )
                : t(
                    "requests.form.create_title"
                  )}
            </h3>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              busy
            }
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {selectedPerson && (
          <div className="personnel-absence-selected-person">
            <span className="personnel-absence-selected-person-icon">
              {personType ===
              "DRIVER"
                ? (
                  <LuTruck />
                )
                : (
                  <LuUser />
                )}
            </span>

            <div>
              <strong>
                {
                  selectedPerson.displayName
                }
              </strong>

              <span>
                {personType ===
                "DRIVER"
                  ? t(
                      "requests.filters.drivers"
                    )
                  : t(
                      "requests.filters.users"
                    )}
              </span>
            </div>
          </div>
        )}

        <div className="personnel-absence-form-grid">
          <label className="personnel-absence-field">
            <span>
              {t(
                "requests.form.person_type"
              )}
            </span>

            <select
              value={
                personType
              }
              onChange={(
                event
              ) => {
                setPersonType(
                  event.target
                    .value
                );

                setPersonId(
                  ""
                );
              }}
              disabled={
                busy
              }
            >
              <option value="USER">
                {t(
                  "requests.filters.users"
                )}
              </option>

              <option value="DRIVER">
                {t(
                  "requests.filters.drivers"
                )}
              </option>
            </select>
          </label>

          <label className="personnel-absence-field">
            <span>
              {t(
                "requests.form.person"
              )}
            </span>

            <select
              value={
                personId
              }
              onChange={(
                event
              ) =>
                setPersonId(
                  event.target
                    .value
                )
              }
              disabled={
                busy
              }
            >
              <option value="">
                {t(
                  "requests.form.select_person"
                )}
              </option>

              {availablePeople.map(
                (
                  person
                ) => (
                  <option
                    key={`${person.personType}:${person.personId}`}
                    value={
                      person.personId
                    }
                  >
                    {
                      person.displayName
                    }
                  </option>
                )
              )}
            </select>
          </label>

          <label className="personnel-absence-field">
            <span>
              {t(
                "requests.form.absence_type"
              )}
            </span>

            <select
              value={
                absenceType
              }
              onChange={(
                event
              ) =>
                setAbsenceType(
                  event.target
                    .value
                )
              }
              disabled={
                busy
              }
            >
              {ABSENCE_TYPES.map(
                (
                  type
                ) => (
                  <option
                    key={
                      type
                    }
                    value={
                      type
                    }
                  >
                    {t(
                      `absence_types.${type}`,
                      type
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="personnel-absence-checkbox">
            <input
              type="checkbox"
              checked={
                isFullDay
              }
              onChange={(
                event
              ) =>
                setIsFullDay(
                  event.target
                    .checked
                )
              }
              disabled={
                busy
              }
            />

            <span>
              <LuCalendarDays />

              {t(
                "requests.form.full_day"
              )}
            </span>
          </label>

          <label className="personnel-absence-field">
            <span>
                {t(
                "requests.form.start_date"
                )}
            </span>

            <div className="personnel-absence-input-with-icon">
                <LuCalendarDays className="personnel-absence-field-icon" />

                <input
                type="date"
                className="personnel-absence-input-has-icon"
                value={
                    startDate
                }
                onChange={(
                    event
                ) =>
                    setStartDate(
                    event.target
                        .value
                    )
                }
                disabled={
                    busy
                }
                />
            </div>
          </label>

          <label className="personnel-absence-field">
            <span>
                {t(
                "requests.form.end_date"
                )}
            </span>

            <div className="personnel-absence-input-with-icon">
                <LuCalendarDays className="personnel-absence-field-icon" />

                <input
                type="date"
                className="personnel-absence-input-has-icon"
                value={
                    endDate
                }
                min={
                    startDate ||
                    undefined
                }
                onChange={(
                    event
                ) =>
                    setEndDate(
                    event.target
                        .value
                    )
                }
                disabled={
                    busy
                }
                />
            </div>
          </label>

          {!isFullDay && (
            <>
              <label className="personnel-absence-field">
                <span>
                  {t(
                    "requests.form.start_time"
                  )}
                </span>

                <div className="personnel-absence-input-with-icon">
                  <LuClock3 className="personnel-absence-field-icon" />

                  <input
                    type="time"
                    className="personnel-absence-input-has-icon"
                    value={
                      startTime
                    }
                    onChange={(
                      event
                    ) =>
                      setStartTime(
                        event.target
                          .value
                      )
                    }
                    disabled={
                      busy
                    }
                  />
                </div>
              </label>

              <label className="personnel-absence-field">
                <span>
                  {t(
                    "requests.form.end_time"
                  )}
                </span>

                <div className="personnel-absence-input-with-icon">
                  <LuClock3 className="personnel-absence-field-icon" />

                  <input
                    type="time"
                    className="personnel-absence-input-has-icon"
                    value={
                      endTime
                    }
                    onChange={(
                      event
                    ) =>
                      setEndTime(
                        event.target
                          .value
                      )
                    }
                    disabled={
                      busy
                    }
                  />
                </div>
              </label>
            </>
          )}

          <label className="personnel-absence-field personnel-absence-field--wide">
            <span>
              {t(
                "requests.form.reason"
              )}
            </span>

            <textarea
              value={
                reason
              }
              onChange={(
                event
              ) =>
                setReason(
                  event.target
                    .value
                )
              }
              rows={3}
              placeholder={t(
                "requests.form.reason_placeholder"
              )}
              disabled={
                busy
              }
            />
          </label>

          <label className="personnel-absence-field personnel-absence-field--wide">
            <span>
              {t(
                "requests.form.private_notes"
              )}
            </span>

            <textarea
              value={
                privateNotes
              }
              onChange={(
                event
              ) =>
                setPrivateNotes(
                  event.target
                    .value
                )
              }
              rows={2}
              placeholder={t(
                "requests.form.private_notes_placeholder"
              )}
              disabled={
                busy
              }
            />
          </label>
        </div>

        {error && (
          <div className="personnel-absence-form-error">
            {
              error
            }
          </div>
        )}

        {confirmDelete ? (
          <div className="personnel-absence-delete-confirm">
            <p>
              {t(
                "requests.form.confirm_delete"
              )}
            </p>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={() =>
                  setConfirmDelete(
                    false
                  )
                }
                disabled={
                  busy
                }
              >
                {t(
                  "requests.actions.back"
                )}
              </button>

              <button
                type="button"
                className="personnel-request-action-btn is-reject"
                onClick={
                  handleDelete
                }
                disabled={
                  busy
                }
              >
                {deleting
                  ? t(
                      "requests.actions.working"
                    )
                  : t(
                      "requests.form.delete_confirm_button"
                    )}
              </button>
            </div>
          </div>
        ) : (
          <div className="personnel-absence-form-actions">
            <div>
              {canDelete &&
                isEdit &&
                request.status ===
                  "PENDING" && (
                  <button
                    type="button"
                    className="personnel-absence-delete-btn"
                    onClick={() =>
                      setConfirmDelete(
                        true
                      )
                    }
                    disabled={
                      busy
                    }
                  >
                    <LuTrash2 />

                    {t(
                      "requests.form.delete"
                    )}
                  </button>
                )}
            </div>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={
                  onClose
                }
                disabled={
                  busy
                }
              >
                {t(
                  "requests.form.close"
                )}
              </button>

              <button
                type="button"
                className="personnel-absence-save-btn"
                onClick={
                  handleSave
                }
                disabled={
                  busy
                }
              >
                <LuSave />

                {saving
                  ? t(
                      "requests.actions.working"
                    )
                  : t(
                      "requests.form.save"
                    )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}